import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Check, ArrowRight, Loader2, Shield, Zap, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isPro } = useAuth();
  const { toast } = useToast();
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [loading, setLoading] = useState(false);

  const plans = {
    monthly: { id: 'monthly', name: 'Mensuel', price: '9.99', period: '/mois', total: '9.99€/mois' },
    yearly: { id: 'yearly', name: 'Annuel', price: '99.99', period: '/an', total: '99.99€/an', savings: 'Économisez 17%' }
  };

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      toast({ title: 'Connexion requise', description: 'Veuillez vous connecter pour passer Pro', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        package_id: selectedPlan,
        origin_url: window.location.origin,
        email: user?.email,
        user_id: user?.id
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Impossible de créer la session de paiement', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  if (isPro) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
        <NavigationMenu />
        <main className="pt-28 pb-20 px-6 flex items-center justify-center">
          <div className="bg-slate-800/50 border border-yellow-500/30 rounded-3xl p-10 max-w-md w-full text-center">
            <Crown className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-white mb-2">Vous êtes déjà Pro !</h1>
            <p className="text-blue-300/70 mb-6">Profitez de toutes les fonctionnalités avancées de SENDBOX.</p>
            <Button onClick={() => navigate('/dashboard')} className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold py-5">
              Aller au tableau de bord
            </Button>
          </div>
        </main>
        <DynamicFooter fixed={true} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
      <NavigationMenu />
      <Toaster />

      <main className="pt-28 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/10 border border-yellow-500/20 rounded-full text-yellow-300 text-sm mb-6">
              <Crown className="w-4 h-4" />
              Passer Pro
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Débloquez tout le potentiel de <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-400">SENDBOX</span>
            </h1>
            <p className="text-blue-200/70 text-lg max-w-2xl mx-auto">
              Fichiers illimités, fonds personnalisés, statistiques détaillées et bien plus encore.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto mb-10">
            {Object.values(plans).map(plan => (
              <button
                key={plan.id}
                onClick={() => setSelectedPlan(plan.id)}
                className={`relative rounded-2xl p-6 text-left transition-all ${
                  selectedPlan === plan.id 
                    ? 'bg-gradient-to-br from-blue-600/30 to-cyan-600/30 border-2 border-cyan-400/60 shadow-lg shadow-cyan-500/10' 
                    : 'bg-slate-800/50 border border-blue-500/20 hover:border-blue-500/40'
                }`}
                data-testid={`plan-${plan.id}`}
              >
                {plan.savings && (
                  <span className="absolute -top-3 right-4 bg-green-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    {plan.savings}
                  </span>
                )}
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedPlan === plan.id ? 'border-cyan-400 bg-cyan-400' : 'border-blue-500/40'
                  }`}>
                    {selectedPlan === plan.id && <Check className="w-3 h-3 text-slate-900" />}
                  </div>
                  <span className="text-white font-semibold">{plan.name}</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-white">{plan.price}€</span>
                  <span className="text-blue-300/70">{plan.period}</span>
                </div>
              </button>
            ))}
          </div>

          <div className="max-w-2xl mx-auto">
            <div className="bg-slate-800/30 border border-blue-500/20 rounded-2xl p-6 mb-6">
              <h3 className="text-white font-semibold mb-4">Inclus dans Pro :</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  'Fichiers illimités', 'Expiration personnalisable', 'Fond d\'écran personnalisé',
                  'Statistiques de téléchargement', 'Protection mot de passe', 'Programmation d\'envoi',
                  'Support prioritaire', 'Sans publicité'
                ].map((f, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                    <span className="text-blue-200/80 text-sm">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            <Button
              onClick={handleCheckout}
              disabled={loading || !isAuthenticated}
              className="w-full bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-slate-900 font-bold py-7 text-lg shadow-lg shadow-yellow-500/20 transition-all"
              data-testid="checkout-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Redirection vers le paiement...</span>
              ) : (
                <span className="flex items-center gap-2 justify-center">
                  <Crown className="w-5 h-5" /> Payer {plans[selectedPlan].total} <ArrowRight className="w-5 h-5" />
                </span>
              )}
            </Button>

            {!isAuthenticated && (
              <p className="text-center text-yellow-400/70 text-sm mt-4">
                Vous devez être connecté pour souscrire à l'offre Pro.
              </p>
            )}

            <div className="flex items-center justify-center gap-6 mt-6 text-blue-300/50 text-xs">
              <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> Paiement sécurisé Stripe</span>
              <span className="flex items-center gap-1"><Zap className="w-3 h-3" /> Activation instantanée</span>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Annulation à tout moment</span>
            </div>
          </div>
        </div>
      </main>

      <DynamicFooter fixed={true} />
    </div>
  );
};

export default CheckoutPage;
